<template>
  <v-app>
    <v-main class="deleted-invoice-page">
      <v-container class="py-5">
        <v-row justify="center">
          <v-col cols="12" sm="11" md="10" lg="8">
            <v-card outlined class="deleted-invoice-card">
              <div class="page-header">
                <div class="d-flex align-center">
                  <div class="header-icon">
                    <v-icon color="error">
                      mdi-file-document-remove-outline
                    </v-icon>
                  </div>

                  <div>
                    <div class="page-title">
                      Deleted Invoice
                    </div>

                    <div class="page-subtitle">
                      Public invoice deletion record
                    </div>
                  </div>
                </div>

                <v-chip
                  v-if="invoice"
                  small
                  label
                  color="error"
                  outlined
                  class="font-weight-bold"
                >
                  Deleted
                </v-chip>
              </div>

              <v-progress-linear
                v-if="loading"
                indeterminate
                color="primary"
              />

              <v-card-text
                v-if="loading"
                class="text-center py-12"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="48"
                  width="5"
                />

                <div class="mt-4 grey--text">
                  Loading deleted invoice...
                </div>
              </v-card-text>

              <v-card-text
                v-else-if="errorMessage"
                class="py-10"
              >
                <v-alert
                  outlined
                  type="error"
                  class="mb-0"
                >
                  {{ errorMessage }}
                </v-alert>
              </v-card-text>

              <div v-else-if="invoice">
                <v-card-text>
                  <v-alert
                    outlined
                    type="error"
                    icon="mdi-alert-circle-outline"
                    class="mb-5"
                  >
                    This invoice was deleted
                    <span v-if="deletedByName">
                      by {{ deletedByName }}
                    </span>
                    on {{ deletedDate }}.
                  </v-alert>

                  <div class="business-header">
                    <div>
                      <div class="business-name">
                        {{ businessName }}
                      </div>

                      <div
                        v-if="businessAddress"
                        class="business-detail"
                      >
                        {{ businessAddress }}
                      </div>

                      <div
                        v-if="businessPhone"
                        class="business-detail"
                      >
                        {{ businessPhone }}
                      </div>

                      <div
                        v-if="businessEmail"
                        class="business-detail"
                      >
                        {{ businessEmail }}
                      </div>
                    </div>

                    <img
                      v-if="businessLogo"
                      :src="businessLogo"
                      alt="Business Logo"
                      class="business-logo"
                    />
                  </div>

                  <div class="invoice-summary">
                    <div class="summary-item">
                      <div class="summary-label">
                        Invoice Number
                      </div>

                      <div class="summary-value">
                        #{{ invoice.localId }}
                      </div>
                    </div>

                    <div class="summary-item">
                      <div class="summary-label">
                        Invoice Date
                      </div>

                      <div class="summary-value">
                        {{ invoiceDate }}
                      </div>
                    </div>

                    <div class="summary-item">
                      <div class="summary-label">
                        Customer
                      </div>

                      <div class="summary-value">
                        {{ customerName }}
                      </div>
                    </div>

                    <div class="summary-item">
                      <div class="summary-label">
                        Created By
                      </div>

                      <div class="summary-value">
                        {{ createdByName }}
                      </div>
                    </div>

                    <div
                      v-if="salesRepName"
                      class="summary-item"
                    >
                      <div class="summary-label">
                        Sales Rep
                      </div>

                      <div class="summary-value">
                        {{ salesRepName }}
                      </div>
                    </div>

                    <div class="summary-item">
                      <div class="summary-label">
                        Total Amount
                      </div>

                      <div class="summary-value total-value">
                        {{ invoiceTotal | currency }}
                      </div>
                    </div>
                  </div>
                </v-card-text>

                <v-divider />

                <v-card-text>
                  <div class="section-title">
                    Bill To
                  </div>

                  <div class="customer-card">
                    <div class="customer-name">
                      {{ customerName }}
                    </div>

                    <div
                      v-if="customerAddress"
                      class="customer-detail"
                    >
                      {{ customerAddress }}
                    </div>

                    <div
                      v-if="customerPhone"
                      class="customer-detail"
                    >
                      {{ customerPhone }}
                    </div>

                    <div
                      v-if="customerEmail"
                      class="customer-detail"
                    >
                      {{ customerEmail }}
                    </div>
                  </div>
                </v-card-text>

                <v-divider />

                <v-card-text>
                  <div class="section-title">
                    Invoice Items
                  </div>

                  <div class="table-wrapper">
                    <v-simple-table class="invoice-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Product Code</th>
                          <th class="text-center">
                            Quantity
                          </th>
                          <th class="text-right">
                            Price
                          </th>
                          <th class="text-right">
                            Cost
                          </th>
                          <th class="text-right">
                            Total
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr
                          v-for="(item, index) in invoiceDetails"
                          :key="item.id || index"
                        >
                          <td>
                            <div class="item-name">
                              {{ item.name || "Product" }}
                            </div>

                            <div
                              v-if="item.imeiNumber"
                              class="item-detail"
                            >
                              IMEI:
                              {{ item.imeiNumber }}
                            </div>

                            <div
                              v-if="item.serialNumber"
                              class="item-detail"
                            >
                              Serial:
                              {{ item.serialNumber }}
                            </div>
                          </td>

                          <td>
                            {{
                              item.Product &&
                              (
                                item.Product.sku ||
                                item.Product.upc ||
                                item.Product.localId
                              )
                                ? item.Product.sku ||
                                  item.Product.upc ||
                                  item.Product.localId
                                : "-"
                            }}
                          </td>

                          <td class="text-center">
                            {{ getQuantity(item) }}
                          </td>

                          <td class="text-right">
                            {{ Number(item.price || 0) | currency }}
                          </td>

                          <td class="text-right">
                            {{ Number(item.cost || 0) | currency }}
                          </td>

                          <td class="text-right font-weight-bold">
                            {{ getLineTotal(item) | currency }}
                          </td>
                        </tr>

                        <tr v-if="!invoiceDetails.length">
                          <td
                            colspan="6"
                            class="text-center grey--text py-8"
                          >
                            No invoice items available.
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </v-card-text>

                <v-divider />

                <v-card-text>
                  <div class="invoice-totals">
                    <div class="total-row">
                      <span>Subtotal</span>
                      <strong>
                        {{ invoiceSubtotal | currency }}
                      </strong>
                    </div>

                    <div
                      v-if="invoiceDiscount"
                      class="total-row"
                    >
                      <span>Discount</span>
                      <strong>
                        -{{ invoiceDiscount | currency }}
                      </strong>
                    </div>

                    <div
                      v-if="invoiceTax"
                      class="total-row"
                    >
                      <span>Tax</span>
                      <strong>
                        {{ invoiceTax | currency }}
                      </strong>
                    </div>

                    <div class="total-row grand-total">
                      <span>Total</span>
                      <strong>
                        {{ invoiceTotal | currency }}
                      </strong>
                    </div>

                    <div
                      v-if="invoicePayment > 0"
                      class="total-row"
                    >
                      <span>Payment</span>
                      <strong>
                        {{ invoicePayment | currency }}
                      </strong>
                    </div>

                    <div
                      v-if="invoiceDue > 0"
                      class="total-row due-total"
                    >
                      <span>Balance Due</span>
                      <strong>
                        {{ invoiceDue | currency }}
                      </strong>
                    </div>
                  </div>
                </v-card-text>

                <v-divider v-if="invoice.note" />

                <v-card-text v-if="invoice.note">
                  <div class="section-title">
                    Invoice Note
                  </div>

                  <div class="note-box">
                    {{ invoice.note }}
                  </div>
                </v-card-text>

                <v-divider />

                <v-card-text class="view-tracking">
                  <div>
                    <div class="tracking-title">
                      Link Activity
                    </div>

                    <div class="tracking-subtitle">
                      This page has been opened
                      {{ openCount }}
                      {{ openCount === 1 ? "time" : "times" }}.
                    </div>
                  </div>

                  <v-icon color="grey">
                    mdi-eye-outline
                  </v-icon>
                </v-card-text>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import invoiceService from "@/modules/Invoice/service";

export default {
  name: "PublicDeletedInvoice",

  props: {
    token: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      loading: true,
      deletedInvoiceData: null,
      errorMessage: "",
    };
  },

  computed: {
    invoice() {
      return this.deletedInvoiceData?.invoice || null;
    },

    deletedInvoiceLink() {
      return (
        this.deletedInvoiceData?.deletedInvoiceLink ||
        null
      );
    },

    invoiceDetails() {
      return Array.isArray(
        this.invoice?.InvoiceDetails
      )
        ? this.invoice.InvoiceDetails
        : [];
    },

    business() {
      return this.invoice?.Business || {};
    },

    customer() {
      return this.invoice?.Customer || {};
    },

    businessName() {
      return (
        this.business.name ||
        this.business.businessName ||
        "smplsale"
      );
    },

    businessAddress() {
      const parts = [
        this.business.address,
        this.business.address1,
        this.business.city,
        this.business.state,
        this.business.zip,
      ].filter(Boolean);

      return parts.join(", ");
    },

    businessPhone() {
      return (
        this.business.phoneNumber ||
        this.business.businessPhone ||
        ""
      );
    },

    businessEmail() {
      return (
        this.business.email ||
        this.business.invoiceEmail ||
        ""
      );
    },

    businessLogo() {
      return (
        this.business.imageUrl ||
        this.business.logoUrl ||
        ""
      );
    },

    customerName() {
      return (
        this.customer.name ||
        "Walk-in Customer"
      );
    },

    customerAddress() {
      const parts = [
        this.customer.address,
        this.customer.address1,
        this.customer.city,
        this.customer.state,
        this.customer.zip,
      ].filter(Boolean);

      return parts.join(", ");
    },

    customerPhone() {
      return (
        this.customer.cellNumber ||
        this.customer.phoneNumber ||
        ""
      );
    },

    customerEmail() {
      return this.customer.email || "";
    },

    createdByName() {
      return (
        this.invoice?.User?.name ||
        "User"
      );
    },

    salesRepName() {
      return (
        this.invoice?.SalesRep?.name ||
        ""
      );
    },

    deletedByName() {
      return (
        this.invoice?.DeletedByUser?.name ||
        this.invoice?.deletedByUserName ||
        ""
      );
    },

    invoiceDate() {
      const value =
        this.invoice?.date ||
        this.invoice?.createdAt;

      if (!value) {
        return "-";
      }

      return this.$moment(value).format(
        "MM/DD/YYYY hh:mm A"
      );
    },

    deletedDate() {
      const value =
        this.invoice?.deletedAt ||
        this.deletedInvoiceLink?.createdAt;

      if (!value) {
        return "-";
      }

      return this.$moment(value).format(
        "MM/DD/YYYY hh:mm A"
      );
    },

    invoiceSubtotal() {
      return this.invoiceDetails.reduce(
        (total, item) =>
          total + this.getLineTotal(item),
        0
      );
    },

    invoiceDiscount() {
      return Number(
        this.invoice?.discount || 0
      );
    },

    invoiceTax() {
      return Number(
        this.invoice?.tax ||
        this.invoice?.taxAmount ||
        0
      );
    },

    invoiceTotal() {
      return Number(
        this.invoice?.total || 0
      );
    },

    invoicePayment() {
      return Number(
        this.invoice?.payment || 0
      );
    },

    invoiceDue() {
      if (
        this.invoice?.due !== undefined &&
        this.invoice?.due !== null
      ) {
        return Number(this.invoice.due || 0);
      }

      return Math.max(
        0,
        this.invoiceTotal - this.invoicePayment
      );
    },

    openCount() {
      return Number(
        this.deletedInvoiceLink?.openCount || 0
      );
    },
  },

  mounted() {
    this.getDeletedInvoice();
  },

  methods: {
    getQuantity(item) {
      return Number(
        item.totalQty !== undefined
          ? item.totalQty
          : item.qty || 0
      );
    },

    getLineTotal(item) {
      if (
        item.total !== undefined &&
        item.total !== null
      ) {
        return Number(item.total || 0);
      }

      return (
        this.getQuantity(item) *
        Number(item.price || 0)
      );
    },

    async getDeletedInvoice() {
      this.loading = true;
      this.errorMessage = "";

      try {
        const token =
          this.token ||
          this.$route.params.token;

        if (!token) {
          throw new Error(
            "Deleted invoice token is missing."
          );
        }

        const result =
          await invoiceService
            .getDeletedInvoiceByToken(token);

        this.deletedInvoiceData =
          result.data || result;
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.data?.message ||
          error?.message ||
          "Deleted invoice could not be found.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.deleted-invoice-page {
  min-height: 100vh;
  background: #f8fafc;
}

.deleted-invoice-card {
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
}

.page-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header-icon {
  width: 42px;
  height: 42px;
  margin-right: 12px;
  border-radius: 12px;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  color: #0f172a;
  font-size: 22px;
  font-weight: 900;
}

.page-subtitle {
  margin-top: 2px;
  color: #64748b;
  font-size: 13px;
}

.business-header {
  margin-bottom: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.business-name {
  color: #0f172a;
  font-size: 20px;
  font-weight: 900;
}

.business-detail {
  margin-top: 3px;
  color: #64748b;
  font-size: 13px;
}

.business-logo {
  max-width: 140px;
  max-height: 80px;
  object-fit: contain;
}

.invoice-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.summary-item {
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.summary-label {
  color: #64748b;
  font-size: 12px;
}

.summary-value {
  margin-top: 4px;
  color: #0f172a;
  font-size: 15px;
  font-weight: 800;
}

.total-value {
  color: var(--v-primary-base);
  font-size: 18px;
}

.section-title {
  margin-bottom: 12px;
  color: #0f172a;
  font-size: 17px;
  font-weight: 900;
}

.customer-card {
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.customer-name {
  color: #0f172a;
  font-weight: 900;
}

.customer-detail {
  margin-top: 3px;
  color: #64748b;
  font-size: 13px;
}

.table-wrapper {
  overflow-x: auto;
}

.invoice-table {
  min-width: 760px;
}

.invoice-table th {
  color: #475569 !important;
  font-size: 12px !important;
  font-weight: 900 !important;
  text-transform: uppercase;
}

.item-name {
  color: #0f172a;
  font-weight: 800;
}

.item-detail {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
}

.invoice-totals {
  width: 100%;
  max-width: 420px;
  margin-left: auto;
}

.total-row {
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.grand-total {
  color: #0f172a;
  font-size: 18px;
  font-weight: 900;
}

.due-total {
  color: #b91c1c;
  font-size: 16px;
}

.note-box {
  padding: 14px;
  border-radius: 10px;
  background: #f8fafc;
  color: #475569;
  white-space: pre-wrap;
}

.view-tracking {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.tracking-title {
  color: #0f172a;
  font-weight: 800;
}

.tracking-subtitle {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
}

@media (max-width: 960px) {
  .invoice-summary {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .invoice-summary {
    grid-template-columns: 1fr;
  }

  .business-header {
    flex-direction: column;
  }

  .business-logo {
    max-width: 110px;
  }

  .page-title {
    font-size: 20px;
  }

  .invoice-totals {
    max-width: 100%;
  }
}
</style>